import _ from 'lodash';
import { Vue, Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { AjaxResponse, AjaxError } from 'rxjs/ajax';
import * as BrowserUtils from '@/utils/browser';
import ApiMixin from '@/mixins/ApiMixin';
import ValidationText from '@/components/validation_text/ValidationText.vue';
import { i18n } from '@/lang';

/**
 * Admin page
 */
@Component({
    components: {
        ValidationText,
    },
})
export default class Admin extends mixins(ApiMixin) {

    /**
     * Internal state
     */
    state: {[key: string]: any} = {
        url: '',
        urls: {
            total: 0,
            limit: 50,
            currentOffset: 0,
            currentPage: 0,
            currentSortedHeaderName: null,
            currentSortDirection: 'ASC',
            query: {},
            empty: 'none',
            headers: [
                {
                    label: i18n.t('l_shorturls'),
                    name: 'id',
                    sortable: true,
                    id: true,
                },
                {
                    label: i18n.t('l_urls'),
                    name: 'url',
                    sortable: true,
                },
                {
                    label: i18n.t('l_actives'),
                    name: 'active',
                    sortable: true,
                },
                {
                    label: i18n.t('l_actions'),
                    name: 'action',
                }
            ],
        },
        deleteModalHidden: true,
        deletedItem: undefined,
        selectedItem: undefined,
        hideQrCode: true,
        successMessage: '',
        successNotifHidden: true,
        errorNotifHidden: true,
        generateSuccess: false,
    };

    /**
     * Login
     */
    generate() {
        const request$ = this.rocket!.request('url', 'post', null, {
            url: this.state.url,
        });

        this.validationErrors = {};
        request$.subscribe((request: AjaxResponse) => {
            const { success, validationErrors } = this.rocket!.unpackResponse(request);

            this.validationErrors = validationErrors;
            if (success) {
                this.read();
                this.state.url = '';
                this.state.generateSuccess = true;
                setTimeout(() => this.state.generateSuccess = false, 3000);
            }
        }, (err: AjaxError) => {
            const { code } = this.rocket!.unpackError(err);
            this.errors.error = true;
            this.errors.code = code;
            this.errors.codeMsg = 'l_unable_to_generate_url';
            this.state.errorNotifHidden = false;
        });
    }

    /**
     * When page or sort changed
     */
    onPageChanged(info: any) {
        const { currentSortedHeader, currentSortDirection, currentPage, currentOffset } = info;
        this.state.urls.currentSortedHeaderName = currentSortedHeader.name;
        this.state.urls.currentSortDirection = currentSortDirection;
        this.state.urls.currentPage = currentPage;
        this.state.urls.currentOffset = currentOffset;
        this.read();
    }

    /**
     * Read
     */
    read() {
        const $sort = [['system.createdAt', -1]];
        if (this.state.urls.currentSortedHeaderName != null) {
            $sort.unshift(
                [
                    this.state.urls.currentSortedHeaderName,
                    this.state.urls.currentSortDirection === 'ASC' ? 1 : -1
                ]
            );
        }

        const req$ = this.rocket!.request('url', 'gets', null, {
            $sort,
            $limit: this.state.urls.limit,
            $skip: this.state.urls.currentOffset,
            $query: this.state.urls.query,
        });

        req$.subscribe((res: AjaxResponse) => {
            const { response } = this.rocket!.unpackResponse(res);
            const { results = [], total = 0 } = response;
            this.state.urls.total = total;
            this.state.urls.items = results;
            if (total === 0) {
                this.state.urls.empty = 'empty';
            } else {
                this.state.urls.empty = 'none';
            }
        }, (err: AjaxError) => {
            const { code } = this.rocket!.unpackError(err);
            console.log('error', code);
        });
    }

    /**
     * Get short url
     */
    url(id: string): string {
        const url = BrowserUtils.getUrlOrigin();
        return `${url}/${id}`;
    }

    /**
     * Show QRCode modal
     */
    showQrCode(item: any) {
        this.state.selectedItem = item;
        this.state.hideQrCode = false;
    }

    /**
     * Generate QR Code
     */
    getQrCode(item: any) {
        const { id } = item;
        const url = BrowserUtils.getUrlOrigin();
        const encoded = encodeURIComponent(url);

        const [req] = this.rocket!
            .generateRouteAndMethod(`qrcode/${encoded}/${id}`, 'action', null);
        return req;
    }

    /**
     * Copy URL
     */
    copyUrl(item: any) {
        this.$copyText(this.url(item.id)).then(
            () => {
                this.state.successMessage = 'l_copy_ok';
                this.state.successNotifHidden = false;
            },
            () => {
                this.errors.code = 'copy_error';
                this.errors.codeMsg = 'l_unable_to_copy';
                this.state.errorNotifHidden = false;
            }
        );
    }

    /**
     * Get short url
     */
    deleteUrl() {
        const { deleteItem } = this.state;
        const req$ = this.rocket!.request('url', 'del', deleteItem._id);

        req$.subscribe((res: AjaxResponse) => {
            const { success } = this.rocket!.unpackResponse(res);
            this.state.deleteItem = undefined;
            this.state.deleteModalHidden = true;
            if (success) {
                this.read();
            }
        }, (err: AjaxError) => {
            const { code } = this.rocket!.unpackError(err);
            this.errors.error = true;
            this.errors.code = code;
            this.errors.codeMsg = 'l_unable_to_delete_url';
            this.state.errorNotifHidden = false;
        });
    }

    /**
     * Update active
     */
    updateActive(value: boolean, item: any) {
        item.active = value;
        const req$ = this.rocket!.request('url', 'put', item._id, item);

        req$.subscribe((res: AjaxResponse) => {
            const { success } = this.rocket!.unpackResponse(res);
            if (success) {
                this.state.successMessage = 'l_update_ok';
                this.state.successNotifHidden = false;
                this.read();
            }
        }, (err: AjaxError) => {
            const { code } = this.rocket!.unpackError(err);
            this.errors.error = true;
            this.errors.code = code;
            this.errors.codeMsg = 'l_unable_to_update_active';
            this.state.errorNotifHidden = false;
        });
    }

    /**
     * Created
     */
    created() {
        this.read();
    }
}
